import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IApiError } from '@models/iapi';
import { AlertToastService, AnalyticEventName, AnalyticsService } from '@services';
import { GetLightReturnReason, GetLightReturnReasonsData } from '@shared/interfaces/api.interfaces';
import { Unsubscriber } from '@shared/classes/unsubscriber';
import { BehaviorSubject, catchError, filter, finalize, merge, Subscription, take } from 'rxjs';
import { Invoice } from '../../../models/invoice/invoice.model';
import { InvoiceService } from '../../../services/invoices/invoice.service';
import { LightReturnModalComponent } from './light-return-modal.component';
import { LightReturnStateService } from './light-return-state.service';

@Injectable({
  providedIn: 'root',
})
export class LightReturnService extends Unsubscriber {
  get lightReturnInitialised(): boolean {
    return this.state.lightReturnInitialised;
  }

  get shouldReloadGrid$() {
    return this.state.shouldReloadGrid$;
  }

  constructor(private invoiceService: InvoiceService,
              private alertToastService: AlertToastService,
              private dialog: MatDialog,
              private analyticsService: AnalyticsService,
              private state: LightReturnStateService,
  ) {
    super();
  }

  pending: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  pending$ = this.pending.asObservable();

  closeSubscription: Subscription;

  init(invoice: Invoice): void {
    this.analyticsService.track({ name: AnalyticEventName.CLICK_EASY_RETURN });

    this.state.shouldReloadGrid.next(false);
    this.setPending(true);

    this.subscriptions = this.invoiceService.checkLightReturn(invoice.docNumber)
      .pipe(
        catchError((errors) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }
          this.dialog.closeAll();
          this.state.shouldReloadGrid.next(true);
          return null;
        }),
        finalize(() => this.setPending(false)),
      )
      .subscribe((data: GetLightReturnReasonsData) => {
          if (!data) {
            this.alertToastService.pushError('Послуга недоступна');
            this.state.shouldReloadGrid.next(true);
            return;
          }
          this.initLightReturnDialog(data[0].subReasonsLightReturn, invoice);
        },
        (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
            this.state.shouldReloadGrid.next(true);
          }
        });
  }

  private initLightReturnDialog(reasons: GetLightReturnReason[], invoice: Invoice): void {

    const dialogRef = this.dialog.open(LightReturnModalComponent, {
      panelClass: 'no-padding',
      disableClose: true,
    });

    this.state.lightReturnInitialised = true;

    dialogRef.componentInstance.type = 'in';
    dialogRef.componentInstance.invoice = invoice;
    dialogRef.componentInstance.reasons = reasons;

    const keys$ = dialogRef
      .keydownEvents()
      .pipe(filter((e) => e.key === 'Escape'));

    const backdrop$ = dialogRef.backdropClick();
    this.subscriptions = merge(keys$, backdrop$)
      .pipe(take(1))
      .subscribe(() => {
        this.state.shouldReloadGrid.next(false);
        dialogRef.close();
      });
  }

  private setPending(value: boolean): void {
    this.pending.next(value);
  }
}
